// src/components/EsecuzioneCommessaEditor.js
// Chi esegue la commessa: personale interno / subappalto / misto, con l'elenco delle imprese
// esterne (regole in utils/esecuzioneCommessa.js). Usato alla creazione della commessa e nel
// "piano di carico" della commessa.
// Le imprese si scelgono dall'anagrafica «Ditte esterne» (piano Pro, W405; la stessa dei
// Documenti dei subappaltatori del Gold). Una ditta nuova si crea al volo con «+ Nuova
// ditta esterna», così il lavoro affidato si può confrontare tra le ditte. Il nome scritto a
// mano resta solo per chi non può creare ditte (o per le commesse di prima, con il pulsante
// «Aggiungi in anagrafica»).
import React, { useEffect, useState } from "react";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useOrg } from "../contexts/OrgContext";
import { TIPI_ESECUZIONE, conImpreseEsterne } from "../utils/esecuzioneCommessa";
import { useAuth } from "../contexts/AuthContext";
import { useCanSee } from "../hooks/useCanSee";
import { FinestraDitta } from "../pages/DitteEsternePage";
import { nomeNorm } from "../utils/ditteEsterne";
import { useIdoneitaDitte, BadgeIdoneita } from "./IdoneitaDitte";

// Nome scritto a mano (non dall'anagrafica): scelto ora, o già salvato senza collegamento.
const libero = (i) => !!i.libero || (!i.subappaltatoreId && !!i.nome);
const nuovaImpresa = () => ({ id: "imp_" + Math.random().toString(36).slice(2, 10), nome: "", subappaltatoreId: null, lavorazioni: "", uominiGiorno: "" });

export default function EsecuzioneCommessaEditor({ tipo = "interno", onTipo, imprese = [], onImprese, compact = false }) {
  const { effectiveOrgId } = useOrg();
  const { isOrgAdmin, isPlatformOwner } = useAuth();
  const canSee = useCanSee();
  const puoCreare = !!(isOrgAdmin || isPlatformOwner || canSee("ditte-esterne"));
  const [anagrafica, setAnagrafica] = useState([]);
  const [leggibile, setLeggibile] = useState(false);
  const [nuovaPer, setNuovaPer] = useState(null);   // { id impresa, nome iniziale }
  // piano Gold: la ditta scelta ha documenti scaduti o mancanti? (W413)
  const idoneita = useIdoneitaDitte(effectiveOrgId);

  useEffect(() => {
    if (!effectiveOrgId || !conImpreseEsterne(tipo)) return;
    // Senza il piano Gold (o senza permesso) la lettura viene rifiutata: si scrive il nome a mano.
    return onSnapshot(query(collection(db, "subappaltatori"), where("organizationId", "==", effectiveOrgId)),
      s => { setLeggibile(true); setAnagrafica(s.docs.map(d => ({ id: d.id, ...d.data() })).filter(x => x.attivo !== false)
        .sort((a, b) => (a.ragioneSociale || "").localeCompare(b.ragioneSociale || ""))); },
      () => { setLeggibile(false); setAnagrafica([]); });
  }, [effectiveOrgId, tipo]);

  const cambiaTipo = (t) => {
    onTipo(t);
    if (conImpreseEsterne(t) && imprese.length === 0) onImprese([nuovaImpresa()]);
  };
  const aggiorna = (id, campi) => onImprese(imprese.map(i => i.id === id ? { ...i, ...campi } : i));

  const inp = { width: "100%", padding: "6px 9px", border: "1px solid #e5e7eb", borderRadius: 7, fontSize: 12.5, fontFamily: "'Inter',sans-serif", outline: "none", boxSizing: "border-box" };

  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
        {Object.values(TIPI_ESECUZIONE).map(t => (
          <button key={t.id} type="button" onClick={() => cambiaTipo(t.id)}
            style={{ flex: 1, padding: compact ? "6px" : "8px 6px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 700,
              border: `1px solid ${tipo === t.id ? "#7c3aed" : "#e5e7eb"}`, background: tipo === t.id ? "#f5f3ff" : "#fff", color: tipo === t.id ? "#7c3aed" : "#6b7280" }}>
            {t.label}
          </button>
        ))}
      </div>
      <div style={{ fontSize: 11, color: "#6b7280", marginBottom: conImpreseEsterne(tipo) ? 10 : 0 }}>{TIPI_ESECUZIONE[tipo]?.descrizione}</div>

      {conImpreseEsterne(tipo) && (
        <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 10, padding: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 6 }}>Imprese esterne</div>
          {imprese.map((i, n) => (
            <div key={i.id} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: 8, marginBottom: 6 }}>
              <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                {leggibile && (anagrafica.length > 0 || puoCreare) && (
                  <select value={i.subappaltatoreId || (libero(i) ? "__libero" : "")} style={{ ...inp, flex: 1 }}
                    onChange={e => {
                      const v = e.target.value;
                      if (v === "") aggiorna(i.id, { subappaltatoreId: null, nome: "", libero: false });
                      else if (v === "__nuova") setNuovaPer({ id: i.id, nome: libero(i) ? i.nome : "" });
                      else if (v === "__libero") aggiorna(i.id, { subappaltatoreId: null, nome: "", libero: true });
                      else aggiorna(i.id, { subappaltatoreId: v, nome: anagrafica.find(a => a.id === v)?.ragioneSociale || "", libero: false });
                    }}>
                    <option value="">— Scegli la ditta esterna —</option>
                    {anagrafica.map(a => <option key={a.id} value={a.id}>{a.ragioneSociale}</option>)}
                    {puoCreare && <option value="__nuova">+ Nuova ditta esterna…</option>}
                    {(!puoCreare || libero(i)) && <option value="__libero">Altra impresa (scrivi il nome)</option>}
                  </select>
                )}
                {(!leggibile || (anagrafica.length === 0 && !puoCreare) || libero(i)) && (
                  <input value={i.nome} onChange={e => aggiorna(i.id, { nome: e.target.value, subappaltatoreId: null, libero: true })} placeholder={`Impresa ${n + 1} — ragione sociale`} style={{ ...inp, flex: 1 }} />
                )}
                {leggibile && puoCreare && libero(i) && i.nome.trim() && (
                  <button type="button" onClick={() => {
                    // c'è già con lo stesso nome (scritto in un altro modo): si collega e basta
                    const gia = anagrafica.find(a => nomeNorm(a.ragioneSociale) === nomeNorm(i.nome));
                    if (gia) aggiorna(i.id, { subappaltatoreId: gia.id, nome: gia.ragioneSociale, libero: false });
                    else setNuovaPer({ id: i.id, nome: i.nome });
                  }} title="Aggiunge la ditta all'anagrafica e la collega a questa commessa"
                    style={{ border: "1px solid #c4b5fd", background: "#faf5ff", color: "#7c3aed", borderRadius: 7, fontSize: 11.5, fontWeight: 700, padding: "0 8px", cursor: "pointer", whiteSpace: "nowrap" }}>+ In anagrafica</button>
                )}
                <button type="button" onClick={() => onImprese(imprese.filter(x => x.id !== i.id))} title="Togli"
                  style={{ border: "none", background: "transparent", color: "#9ca3af", cursor: "pointer", fontSize: 14 }}>✕</button>
              </div>
              {i.subappaltatoreId && idoneita?.get(i.subappaltatoreId) && <div style={{ marginBottom: 6 }}><BadgeIdoneita info={idoneita.get(i.subappaltatoreId)} /></div>}
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 6, fontSize: 10, fontWeight: 600, color: "#9ca3af", marginBottom: 2 }}>
                <span>Lavorazioni affidate</span><span>Uomini-giorno presunti</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 6 }}>
                <input value={i.lavorazioni} onChange={e => aggiorna(i.id, { lavorazioni: e.target.value })} placeholder="Lavorazioni affidate (es. scavi, montaggio strutture)" style={inp} />
                <input value={i.uominiGiorno ?? ""} onChange={e => aggiorna(i.id, { uominiGiorno: e.target.value })} placeholder="Uomini-giorno presunti" title="Persone × giorni di lavoro previsti per questa impresa (serve anche alla sicurezza di cantiere)" style={inp} />
              </div>
            </div>
          ))}
          {nuovaPer && (
            <FinestraDitta nomeIniziale={nuovaPer.nome} ditte={anagrafica} onClose={() => setNuovaPer(null)}
              onSalvata={(d) => aggiorna(nuovaPer.id, { subappaltatoreId: d.id, nome: d.ragioneSociale, libero: false })} />
          )}
          <button type="button" onClick={() => onImprese([...imprese, nuovaImpresa()])}
            style={{ fontSize: 12, fontWeight: 600, padding: "5px 10px", borderRadius: 7, border: "1px dashed #7c3aed", background: "#fff", color: "#7c3aed", cursor: "pointer" }}>
            + Aggiungi impresa
          </button>
        </div>
      )}
    </div>
  );
}
